/**
 * Inline preview card shown in the chat for `create_artifact` /
 * `update_artifact` / `generate_image` tool parts.
 *
 * Streams a live preview during generation and lets the user click to open
 * the full canvas. Per-kind rendering:
 * - `document`     → markdown body (live appended during stream)
 * - `html`         → CSS-scaled sandboxed iframe of the artifact
 * - `presentation` → CSS-scaled sandboxed iframe of the first slide
 * - `image`        → centered image on a checker backdrop
 * - `spreadsheet`  → mini grid of the first few CSV rows
 *
 * The component name stays `DocumentPreview` for backwards compatibility
 * with existing imports in `messages/message.tsx`.
 *
 * @module components/artifacts/document-preview
 */

import type { ToolUIPart } from "ai";
import {
  FileText,
  Image as ImageIcon,
  LayoutTemplate,
  Loader2,
  Presentation,
  Table2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Markdown } from "@/components/ui/markdown";
import { cn } from "@/lib/utils";
import { parseCsv } from "@/lib/artifacts/csv";
import { effectiveArtifactKind } from "@/lib/artifacts/html-detect";
import { parseImageArtifactContent } from "@/lib/artifacts/image";
import { parseSlides } from "@/lib/artifacts/slides";
import { ARTIFACT_STYLE_PRELUDE_HTML } from "@/lib/artifacts/style-guide";
import { useArtifactStore } from "@/store/artifact-store";
import { ARTIFACT_TOOL_NAMES, type ArtifactKind, type ArtifactToolResult } from "@taqat/core/types";
import "./artifact-editor.css";

/** Scaled-down dimensions used by the html / presentation preview iframes. */
const PREVIEW_HEIGHT = 240;

interface DocumentPreviewProps {
  part: ToolUIPart;
}

/**
 * Inline artifact preview shown beside the assistant message. Falls back to
 * a skeleton while the model is still generating, then renders a kind-aware
 * preview once a body is available. Clicking the card opens the canvas.
 */
export function DocumentPreview({ part }: DocumentPreviewProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const showArtifact = useArtifactStore((s) => s.showArtifact);
  const setArtifact = useArtifactStore((s) => s.setArtifact);
  const liveArtifact = useArtifactStore((s) => s.artifact);

  const input = (part.input ?? {}) as {
    title?: string;
    kind?: ArtifactKind;
    prompt?: string;
  };
  const output = (part.output ?? null) as ArtifactToolResult | null;

  // `generate_image` doesn't pass a kind — infer from the tool name so the
  // skeleton + icon render correctly while the model generates the image.
  const isGenerateImage =
    typeof part.type === "string" &&
    part.type === `tool-${ARTIFACT_TOOL_NAMES.GENERATE_IMAGE}`;

  const id = output?.id ?? "";
  const title =
    output?.title ||
    input.title ||
    (isGenerateImage ? deriveImageTitle(input.prompt) : "Untitled");
  const kind: ArtifactKind =
    output?.kind || input.kind || (isGenerateImage ? "image" : "document");
  const isStreaming =
    !output && liveArtifact.id === id && liveArtifact.status === "streaming";
  // For markdown the streamed body lives in `content`; for HTML / presentation
  // the canvas buffers through `pendingContent` so the preview also pulls
  // from there to keep the user oriented while a new artifact generates.
  const liveBody =
    liveArtifact.id === id
      ? liveArtifact.content || liveArtifact.pendingContent || ""
      : "";
  const body = output?.content || liveBody || "";
  // An HTML page authored under the default `document` kind should preview as
  // the rendered page, not raw markdown. Resolve for display only; the store
  // keeps the declared kind so update/download logic stays correct.
  const displayKind = effectiveArtifactKind(kind, body) as ArtifactKind;

  /**
   * Open the canvas at the click position so the overlay animation has an
   * origin that matches where the user pointed. Pre-populates the store with
   * the known title/content so the overlay shows the right artifact
   * immediately even if the API fetch hasn't completed yet.
   */
  function handleOpen() {
    const rect = cardRef.current?.getBoundingClientRect();
    if (id) {
      setArtifact({ id, title, kind, content: body, status: "idle" });
    }
    showArtifact(
      rect
        ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
        : undefined,
    );
  }

  return (
    <div
      ref={cardRef}
      className="my-3 cursor-pointer rounded-2xl border bg-background"
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleOpen();
      }}
    >
      <div className="flex items-center gap-3 border-b px-4 py-3">
        {isStreaming ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : (
          <KindIcon kind={displayKind} />
        )}
        <div className="font-medium text-sm">{title}</div>
        <KindBadge kind={displayKind} body={body} />
      </div>
      <PreviewBody kind={displayKind} body={body} />
    </div>
  );
}

/** Renders the kind-specific preview body (markdown / iframe / first slide). */
function PreviewBody({ kind, body }: { kind: ArtifactKind; body: string }) {
  if (!body) {
    return (
      <div className="bg-muted/30 px-4 py-3">
        <PreviewSkeleton />
      </div>
    );
  }

  if (kind === "html") {
    return (
      <div
        className="overflow-hidden bg-white"
        style={{ height: PREVIEW_HEIGHT }}
      >
        <iframe
          sandbox="allow-scripts"
          srcDoc={body}
          title="HTML preview"
          className="pointer-events-none h-full w-full border-0"
        />
      </div>
    );
  }

  if (kind === "presentation") {
    return <PresentationPreview body={body} />;
  }

  if (kind === "image") {
    return <ImagePreview body={body} />;
  }

  if (kind === "spreadsheet") {
    return <SpreadsheetPreview body={body} />;
  }

  return (
    <div
      className={cn(
        "max-h-[260px] overflow-hidden bg-muted/30 px-4 py-3",
      )}
    >
      <div className="artifact-content text-sm">
        <Markdown>{body}</Markdown>
      </div>
    </div>
  );
}

/** Centered image preview on a checker backdrop. */
function ImagePreview({ body }: { body: string }) {
  const parsed = useMemo(() => parseImageArtifactContent(body), [body]);
  if (!parsed?.url) {
    return (
      <div className="bg-muted/30 px-4 py-3">
        <PreviewSkeleton />
      </div>
    );
  }
  return (
    <div
      className="relative flex items-center justify-center overflow-hidden p-4"
      style={{
        height: PREVIEW_HEIGHT,
        backgroundColor: "#f1f5f9",
        backgroundImage: `
          linear-gradient(45deg, rgba(148,163,184,0.18) 25%, transparent 25%),
          linear-gradient(-45deg, rgba(148,163,184,0.18) 25%, transparent 25%),
          linear-gradient(45deg, transparent 75%, rgba(148,163,184,0.18) 75%),
          linear-gradient(-45deg, transparent 75%, rgba(148,163,184,0.18) 75%)
        `,
        backgroundSize: "16px 16px",
        backgroundPosition: "0 0, 0 8px, 8px -8px, 8px 0",
      }}
    >
      <img
        src={parsed.url}
        alt={parsed.prompt || "Generated image"}
        loading="lazy"
        draggable={false}
        className="max-h-full max-w-full rounded shadow-md ring-1 ring-black/10 select-none"
      />
    </div>
  );
}

/** Mini grid showing the first few CSV rows. */
function SpreadsheetPreview({ body }: { body: string }) {
  const parsed = useMemo(() => parseCsv(body), [body]);
  if (parsed.headers.length === 0) {
    return (
      <div className="bg-muted/30 px-4 py-3">
        <PreviewSkeleton />
      </div>
    );
  }
  const headers = parsed.headers.slice(0, 6);
  const rows = parsed.rows.slice(0, 5);
  const truncatedColumns = parsed.headers.length > headers.length;
  return (
    <div
      className="relative overflow-hidden bg-background"
      style={{ height: PREVIEW_HEIGHT }}
    >
      <table className="w-full border-collapse text-[11px] leading-tight">
        <thead className="bg-muted text-muted-foreground">
          <tr>
            {headers.map((header, idx) => (
              <th
                key={`${header}-${idx}`}
                className="px-2 py-1 text-left border-r border-b border-border font-semibold whitespace-nowrap truncate max-w-[140px]"
                title={header}
              >
                {header || `col ${idx + 1}`}
              </th>
            ))}
            {truncatedColumns ? (
              <th className="px-2 py-1 border-b border-border font-semibold text-muted-foreground/60">
                +{parsed.headers.length - headers.length}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-muted/30">
              {headers.map((_, colIdx) => (
                <td
                  key={colIdx}
                  className="px-2 py-1 border-r border-b border-border whitespace-nowrap truncate max-w-[140px] align-top"
                  title={row[colIdx] ?? ""}
                >
                  {row[colIdx] ?? ""}
                </td>
              ))}
              {truncatedColumns ? (
                <td className="px-2 py-1 border-b border-border text-muted-foreground/40">…</td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background to-transparent" />
    </div>
  );
}

/** Pulls a short, sentence-like title from a generate_image prompt. */
function deriveImageTitle(prompt: string | undefined): string {
  if (!prompt) return "Generated image";
  const cleaned = prompt.trim().replace(/\s+/g, " ").slice(0, 64);
  return cleaned || "Generated image";
}

/** Native slide canvas the deck HTML is authored against. */
const SLIDE_WIDTH = 1280;
const SLIDE_HEIGHT = 720;

/**
 * First-slide thumbnail for presentation artifacts.
 *
 * The slide is rendered at its native 1280×720 inside a sandboxed iframe and
 * scaled down to the card. The scale MUST be measured rather than assumed: a
 * fixed factor only ever fits one card width, and the chat column is fluid, so
 * a hardcoded value left a slide floating in whitespace on wide screens and
 * cropped it on narrow ones.
 */
function PresentationPreview({ body }: { body: string }) {
  const slides = useMemo(() => parseSlides(body), [body]);
  const first = slides[0];
  const frameRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(0);

  // Measured, not assumed — and re-measured on resize, since the chat column
  // changes width when the canvas panel opens.
  useEffect(() => {
    const element = frameRef.current;
    if (!element) return;

    const measure = () => {
      const width = element.clientWidth;
      if (width > 0) setScale(width / SLIDE_WIDTH);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [first]);

  if (!first) {
    return (
      <div className="bg-muted/30 px-4 py-3">
        <PreviewSkeleton />
      </div>
    );
  }
  const wrapper = `<!doctype html><html><head>
    <meta charset="utf-8" />
    ${ARTIFACT_STYLE_PRELUDE_HTML}
    <script src="https://cdn.tailwindcss.com"></script>
    <style>html,body{margin:0;padding:0;width:1280px;height:720px;overflow:hidden}body>section{width:1280px !important;height:720px !important;max-width:none !important;min-width:0 !important;max-height:none !important;min-height:0 !important;box-sizing:border-box}</style>
  </head><body>${first.html}</body></html>`;
  // Scaling to the card's WIDTH makes the slide taller than the card's fixed
  // height, so the band that shows is chosen deliberately: centre it, because
  // slide content sits in the middle and cropping from the top would show the
  // empty margin above a centred title.
  const offsetY = (PREVIEW_HEIGHT - SLIDE_HEIGHT * scale) / 2;

  return (
    <div
      ref={frameRef}
      className="relative w-full overflow-hidden bg-white"
      style={{ height: PREVIEW_HEIGHT }}
    >
      {/* Held back until the first measurement so the slide never flashes at
          the wrong size before settling. */}
      {scale > 0 ? (
        <iframe
          sandbox="allow-scripts"
          srcDoc={wrapper}
          title="Presentation preview"
          className="pointer-events-none absolute left-0 top-0 origin-top-left border-0"
          style={{
            width: SLIDE_WIDTH,
            height: SLIDE_HEIGHT,
            transform: `translateY(${offsetY}px) scale(${scale})`,
          }}
        />
      ) : null}
    </div>
  );
}

function KindIcon({ kind }: { kind: ArtifactKind }) {
  if (kind === "html") {
    return <LayoutTemplate className="size-4 text-muted-foreground" />;
  }
  if (kind === "presentation") {
    return <Presentation className="size-4 text-muted-foreground" />;
  }
  if (kind === "image") {
    return <ImageIcon className="size-4 text-muted-foreground" />;
  }
  if (kind === "spreadsheet") {
    return <Table2 className="size-4 text-muted-foreground" />;
  }
  return <FileText className="size-4 text-muted-foreground" />;
}

function KindBadge({ kind, body }: { kind: ArtifactKind; body: string }) {
  if (kind === "presentation") {
    const slideCount = body ? parseSlides(body).length : 0;
    if (slideCount === 0) return null;
    return (
      <span className="ml-auto rounded bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {slideCount} slide{slideCount === 1 ? "" : "s"}
      </span>
    );
  }
  if (kind === "html") {
    return (
      <span className="ml-auto rounded bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        HTML
      </span>
    );
  }
  if (kind === "image") {
    return (
      <span className="ml-auto rounded bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Image
      </span>
    );
  }
  if (kind === "spreadsheet") {
    const rowCount = body ? parseCsv(body).rows.length : 0;
    return (
      <span className="ml-auto rounded bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {rowCount > 0 ? `${rowCount} row${rowCount === 1 ? "" : "s"}` : "CSV"}
      </span>
    );
  }
  return null;
}

function PreviewSkeleton() {
  return (
    <div className="space-y-2">
      <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
      <div className="h-3 w-full animate-pulse rounded bg-muted" />
      <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
    </div>
  );
}
